import { useState } from 'react';
import { 
  Globe, ArrowRight, ArrowLeft, Check, Loader2, 
  Image, Calendar, Users, Quote, FileText, 
  Phone, Link as LinkIcon, Video, HelpCircle,
  AlertCircle, RefreshCw, CheckCircle2, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

// Module icons mapping
const moduleIcons = {
  home: Globe,
  about: FileText,
  gallery: Image,
  events: Calendar,
  social_links: LinkIcon,
  video_gallery: Video,
  news: FileText,
  contact: Phone,
  press: Quote,
  music: Video,
  mailing_list: Users,
  services: FileText,
  team: Users,
  testimonials: Quote,
  faq: HelpCircle,
};

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => (
      <div key={step.id} className="flex items-center">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all",
          currentStep > index 
            ? "bg-emerald-500 text-white" 
            : currentStep === index 
              ? "bg-orange-500 text-white ring-4 ring-orange-100" 
              : "bg-slate-200 text-slate-500"
        )}>
          {currentStep > index ? <Check className="w-5 h-5" /> : index + 1}
        </div>
        {index < steps.length - 1 && (
          <div className={cn(
            "w-16 h-1 mx-2",
            currentStep > index ? "bg-emerald-500" : "bg-slate-200"
          )} />
        )}
      </div>
    ))}
  </div>
);

// Step 1: Enter URL
const Step1EnterUrl = ({ url, setUrl, onScan, isScanning }) => (
  <div className="max-w-xl mx-auto text-center space-y-8">
    <div>
      <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
        <Globe className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Import Existing Website</h2>
      <p className="text-slate-500">
        Enter the URL of a performer website and we'll automatically extract its content 
        to create a new site in Boxit.
      </p>
    </div>

    <div className="space-y-4">
      <div className="relative">
        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.performer-website.com"
          className="pl-12 h-14 text-lg"
          data-testid="import-url-input"
        />
      </div>
      
      <Button 
        onClick={onScan} 
        disabled={!url || isScanning}
        className="w-full h-14 text-lg bg-orange-500 hover:bg-orange-600"
        data-testid="scan-website-btn"
      >
        {isScanning ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Scanning Website...
          </>
        ) : (
          <>
            Scan Website
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>

    <div className="bg-slate-50 rounded-lg p-4 text-left">
      <h4 className="font-medium text-slate-700 mb-2">What gets imported:</h4>
      <ul className="text-sm text-slate-500 space-y-1">
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Images and gallery photos</li>
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> About/bio text content</li>
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Events and tour dates</li>
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Social media links</li>
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Contact information</li>
        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Videos and press quotes</li>
      </ul>
    </div>
  </div>
);

// Step 2: Review & Edit scraped data
const Step2ReviewData = ({ scrapedData, formData, setFormData }) => {
  const detectedModules = [];
  
  if (scrapedData.about_text) detectedModules.push({ id: 'about', name: 'About', count: 1 });
  if (scrapedData.gallery_images?.length || scrapedData.images?.length) {
    detectedModules.push({ id: 'gallery', name: 'Gallery', count: scrapedData.gallery_images?.length || scrapedData.images?.length });
  }
  if (scrapedData.events?.length) detectedModules.push({ id: 'events', name: 'Events', count: scrapedData.events.length });
  if (scrapedData.social_links?.length) detectedModules.push({ id: 'social_links', name: 'Social Links', count: scrapedData.social_links.length });
  if (scrapedData.videos?.length) detectedModules.push({ id: 'video_gallery', name: 'Videos', count: scrapedData.videos.length });
  if (scrapedData.news_items?.length) detectedModules.push({ id: 'news', name: 'News', count: scrapedData.news_items.length });
  if (scrapedData.press_quotes?.length) detectedModules.push({ id: 'press', name: 'Press', count: scrapedData.press_quotes.length });
  if (scrapedData.contact_email || scrapedData.contact_phone) detectedModules.push({ id: 'contact', name: 'Contact', count: 1 });
  if (scrapedData.services?.length) detectedModules.push({ id: 'services', name: 'Services', count: scrapedData.services.length });
  if (scrapedData.team_members?.length) detectedModules.push({ id: 'team', name: 'Team', count: scrapedData.team_members.length });
  if (scrapedData.testimonials?.length) detectedModules.push({ id: 'testimonials', name: 'Testimonials', count: scrapedData.testimonials.length });
  if (scrapedData.faq_items?.length) detectedModules.push({ id: 'faq', name: 'FAQ', count: scrapedData.faq_items.length });

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Review Detected Content</h2>
        <p className="text-slate-500">
          We found the following content. Edit the basic info and select which modules to import.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left column - Basic Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
              <CardDescription>Verify and edit the detected details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Performer Name *</Label>
                <Input
                  value={formData.performer_name}
                  onChange={(e) => setFormData({ ...formData, performer_name: e.target.value })}
                  placeholder="Performer or business name"
                  data-testid="import-performer-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tagline</Label>
                <Textarea
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="Short description or tagline"
                  rows={2}
                  data-testid="import-tagline"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(v) => setFormData({ ...formData, service_type: v })}
                >
                  <SelectTrigger data-testid="import-service-type">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Entertainer">Entertainer</SelectItem>
                    <SelectItem value="Magician">Magician</SelectItem>
                    <SelectItem value="Singer">Singer</SelectItem>
                    <SelectItem value="Actor">Actor/Actress</SelectItem>
                    <SelectItem value="DJ">DJ</SelectItem>
                    <SelectItem value="Band">Band</SelectItem>
                    <SelectItem value="Comedian">Comedian</SelectItem>
                    <SelectItem value="Children's Entertainer">Children's Entertainer</SelectItem>
                    <SelectItem value="Dance School">Dance School</SelectItem>
                    <SelectItem value="Stage School">Stage School</SelectItem>
                    <SelectItem value="Photographer">Photographer</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Package</Label>
                <Select 
                  value={formData.package} 
                  onValueChange={(v) => setFormData({ ...formData, package: v })}
                >
                  <SelectTrigger data-testid="import-package">
                    <SelectValue placeholder="Select package" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter (Basic modules)</SelectItem>
                    <SelectItem value="pro">Pro (+ Events, Videos, Music)</SelectItem>
                    <SelectItem value="max">Max (All modules)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Logo and Hero Background Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Brand Assets</CardTitle>
              <CardDescription>Detected logo and hero background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Logo */}
                <div>
                  <Label className="text-sm text-slate-500 mb-2 block">Logo</Label>
                  {scrapedData.logo?.url ? (
                    <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 border p-2 flex items-center justify-center">
                      <img 
                        src={scrapedData.logo.url} 
                        alt={scrapedData.logo.alt || 'Logo'} 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => e.target.parentElement.innerHTML = '<span class="text-slate-400 text-sm">Failed to load</span>'}
                      />
                    </div>
                  ) : scrapedData.logo?.text ? (
                    <div className="aspect-square rounded-lg bg-slate-900 border flex items-center justify-center p-4">
                      <span className="text-white text-xl font-bold text-center">{scrapedData.logo.text}</span>
                    </div>
                  ) : (
                    <div className="aspect-square rounded-lg bg-slate-100 border flex items-center justify-center">
                      <span className="text-slate-400 text-sm">No logo detected</span>
                    </div>
                  )}
                  {scrapedData.logo?.text && !scrapedData.logo?.url && (
                    <p className="text-xs text-slate-500 mt-1">Text-based logo detected</p>
                  )}
                </div>
                
                {/* Hero Background */}
                <div>
                  <Label className="text-sm text-slate-500 mb-2 block">Hero Background</Label>
                  {scrapedData.hero_background ? (
                    <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 border">
                      <img 
                        src={scrapedData.hero_background} 
                        alt="Hero background" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.parentElement.innerHTML = '<span class="text-slate-400 text-sm p-4 block text-center">Failed to load</span>'}
                      />
                    </div>
                  ) : (
                    <div className="aspect-square rounded-lg bg-slate-100 border flex items-center justify-center">
                      <span className="text-slate-400 text-sm">No hero image detected</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Detected Fonts */}
              {scrapedData.fonts?.length > 0 && (
                <div>
                  <Label className="text-sm text-slate-500 mb-2 block">Detected Fonts</Label>
                  <div className="flex flex-wrap gap-2">
                    {scrapedData.fonts.map((font, i) => (
                      <Badge key={i} variant="outline" className="bg-slate-50">
                        {font}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preview images */}
          {scrapedData.images?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Gallery Images</CardTitle>
                <CardDescription>{scrapedData.gallery_images?.length || scrapedData.images.length} images found</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2">
                  {(scrapedData.gallery_images?.length ? scrapedData.gallery_images : scrapedData.images.map(i => i.url)).slice(0, 8).map((img, i) => (
                    <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img 
                        src={typeof img === 'string' ? img : img.url} 
                        alt="" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  ))}
                </div>
                {(scrapedData.gallery_images?.length || scrapedData.images.length) > 8 && (
                  <p className="text-sm text-slate-500 mt-2 text-center">
                    +{(scrapedData.gallery_images?.length || scrapedData.images.length) - 8} more images
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - Modules Selection */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detected Modules</CardTitle>
              <CardDescription>Select which content to import</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Always include home */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Home</p>
                      <p className="text-sm text-slate-500">Hero section (required)</p>
                    </div>
                  </div>
                  <Check className="w-5 h-5 text-orange-500" />
                </div>

                {detectedModules.map((module) => {
                  const Icon = moduleIcons[module.id] || FileText;
                  const isSelected = formData.selected_modules.includes(module.id);
                  
                  return (
                    <div 
                      key={module.id}
                      onClick={() => {
                        if (isSelected) {
                          setFormData({
                            ...formData,
                            selected_modules: formData.selected_modules.filter(m => m !== module.id)
                          });
                        } else {
                          setFormData({
                            ...formData,
                            selected_modules: [...formData.selected_modules, module.id]
                          });
                        }
                      }}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all",
                        isSelected 
                          ? "bg-emerald-50 border-emerald-200" 
                          : "bg-white border-slate-200 hover:border-slate-300"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          isSelected ? "bg-emerald-500" : "bg-slate-200"
                        )}>
                          <Icon className={cn("w-5 h-5", isSelected ? "text-white" : "text-slate-500")} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{module.name}</p>
                          <p className="text-sm text-slate-500">{module.count} item(s) found</p>
                        </div>
                      </div>
                      <Checkbox 
                        checked={isSelected}
                        className="pointer-events-none"
                      />
                    </div>
                  );
                })}

                {detectedModules.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No additional content detected</p>
                    <p className="text-sm">The basic site structure will be imported</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Scraped text preview */}
          {scrapedData.about_text && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">About Text Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 line-clamp-4">{scrapedData.about_text}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 3: Confirm & Create
const Step3Confirm = ({ formData, scrapedData, isCreating }) => (
  <div className="max-w-2xl mx-auto space-y-8">
    <div className="text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
        <Check className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Ready to Import</h2>
      <p className="text-slate-500">
        Review the summary below and click "Create Site" to import this website into Boxit.
      </p>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Import Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-slate-500">Performer Name</p>
            <p className="font-semibold text-slate-900">{formData.performer_name}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Service Type</p>
            <p className="font-semibold text-slate-900">{formData.service_type}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Package</p>
            <Badge className="capitalize">{formData.package}</Badge>
          </div>
          <div>
            <p className="text-sm text-slate-500">Source URL</p>
            <a href={scrapedData.url} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline text-sm truncate block">
              {scrapedData.url}
            </a>
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm text-slate-500 mb-3">Modules to Import</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="bg-orange-50">Home</Badge>
            {formData.selected_modules.map(mod => (
              <Badge key={mod} variant="outline" className="bg-emerald-50 capitalize">
                {mod.replace('_', ' ')}
              </Badge>
            ))}
          </div>
        </div>

        {formData.tagline && (
          <div className="border-t pt-4">
            <p className="text-sm text-slate-500 mb-1">Tagline</p>
            <p className="text-slate-700">{formData.tagline}</p>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Site will be created as Draft</p>
              <p className="text-sm text-amber-700">
                You'll be able to review and edit all content before publishing the site.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Main Migration Wizard Component
export default function MigrationWizard({ onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [url, setUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [formData, setFormData] = useState({
    performer_name: '',
    tagline: '',
    service_type: 'Entertainer',
    package: 'max',
    selected_modules: []
  });

  const steps = [
    { id: 'url', title: 'Enter URL' },
    { id: 'review', title: 'Review Content' },
    { id: 'confirm', title: 'Confirm' }
  ];

  const handleScan = async () => {
    if (!url) return;
    
    // Ensure URL has protocol
    let cleanUrl = url.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    setIsScanning(true);
    try {
      const response = await api.post('/import/preview', { url: cleanUrl });
      setScrapedData(response.data);
      
      // Pre-populate form with detected data
      const detectedModules = [];
      if (response.data.about_text) detectedModules.push('about');
      if (response.data.gallery_images?.length || response.data.images?.length) detectedModules.push('gallery');
      if (response.data.events?.length) detectedModules.push('events');
      if (response.data.social_links?.length) detectedModules.push('social_links');
      if (response.data.videos?.length) detectedModules.push('video_gallery');
      if (response.data.news_items?.length) detectedModules.push('news');
      if (response.data.press_quotes?.length) detectedModules.push('press');
      if (response.data.contact_email || response.data.contact_phone) detectedModules.push('contact');
      
      setFormData({
        performer_name: response.data.detected_name || '',
        tagline: response.data.tagline || '',
        service_type: response.data.service_type || 'Entertainer',
        package: 'max',
        selected_modules: detectedModules
      });
      
      setCurrentStep(1);
      toast.success('Website scanned successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan website');
    } finally {
      setIsScanning(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.performer_name) {
      toast.error('Please enter a performer name');
      return;
    }
    
    setIsCreating(true);
    try {
      const response = await api.post('/import/create', {
        url: scrapedData.url,
        performer_name: formData.performer_name,
        tagline: formData.tagline,
        service_type: formData.service_type,
        package: formData.package,
        selected_modules: ['home', ...formData.selected_modules],
        scraped_data: scrapedData
      });
      
      toast.success('Site imported successfully!');
      onComplete?.(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Site Migration Wizard</h1>
                <p className="text-xs text-slate-500 -mt-0.5">Import existing websites</p>
              </div>
            </div>
            
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-migration-wizard">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <StepIndicator currentStep={currentStep} steps={steps} />

        {/* Step Content */}
        <div className="mt-8">
          {currentStep === 0 && (
            <Step1EnterUrl 
              url={url}
              setUrl={setUrl}
              onScan={handleScan}
              isScanning={isScanning}
            />
          )}
          
          {currentStep === 1 && scrapedData && (
            <Step2ReviewData 
              scrapedData={scrapedData}
              formData={formData}
              setFormData={setFormData}
            />
          )}
          
          {currentStep === 2 && (
            <Step3Confirm 
              formData={formData}
              scrapedData={scrapedData}
              isCreating={isCreating}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t">
          <Button
            variant="outline"
            onClick={() => currentStep === 0 ? onClose() : setCurrentStep(currentStep - 1)}
            disabled={isCreating}
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>

          {currentStep < 2 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={currentStep === 0 || !scrapedData}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="wizard-next-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={isCreating}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="wizard-create-btn"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Site...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Site
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
